import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAppliedLineage } from '@/hooks/dbtStrategy/useAppliedLineage';
import { MAX_PAGE_SIZE } from '@/hooks/discovery/apiConstants';
import { usePublicModels } from '@/hooks/discovery/publicModels';
import { ColumnPanelDetails } from './ColumnPanelDetails';
import { ColumnPanelHeader } from './ColumnPanelHeader';

export type ColumnPanelContentsProps = {
  nodeUniqueId: string | undefined;
  column: string | undefined;
  environmentId: number | undefined;
};

export const ColumnPanelContents: React.FC<ColumnPanelContentsProps> = ({
  nodeUniqueId,
  column,
  environmentId: panelEnvironmentId,
}) => {
  const { accountId, environmentId: currentEnvironmentId } = useDbtCloudProject();

  // find the node either from current project's lineage or public models
  const lineage = useAppliedLineage({
    environmentId: currentEnvironmentId,
    uniqueIds: [nodeUniqueId!],
    pause: !nodeUniqueId,
  });
  const currentProjectNode = lineage.data?.environment?.applied?.lineage?.find(
    (x) => x.uniqueId === nodeUniqueId,
  );

  const publicModels = usePublicModels({
    first: MAX_PAGE_SIZE,
    accountId,
    environmentIds: [panelEnvironmentId!],
    uniqueIds: [nodeUniqueId!],
    pause: !!currentProjectNode || !nodeUniqueId || !panelEnvironmentId,
  });
  const publicNode = publicModels.data?.find(
    (model) => model.uniqueId === nodeUniqueId,
  );

  if (!nodeUniqueId || !column) return null;
  return (
    <div
      className="h-full overflow-y-auto overflow-x-hidden"
      data-testid="column-panel"
    >
      <ColumnPanelHeader
        nodeUniqueId={nodeUniqueId}
        column={column}
        files={[currentProjectNode?.filePath]}
        projectId={currentProjectNode?.projectId || publicNode?.projectId || undefined}
        environmentId={panelEnvironmentId}
      />
      <ColumnPanelDetails
        nodeUniqueId={nodeUniqueId}
        columnName={column}
        projectId={currentProjectNode?.projectId || publicNode?.projectId || undefined}
        environmentId={panelEnvironmentId}
      />
    </div>
  );
};
